// Redaction for values written to error_logs. Pure, so it can be unit
// tested without the Supabase client.

const REDACTED = "[REDACTED]";
const MAX_DEPTH = 8;

const SENSITIVE_KEY_PARTS = [
  "password",
  "passwd",
  "secret",
  "token",
  "authorization",
  "apikey",
  "api_key",
  "credit_card",
  "card_number",
  "cardnumber",
  "cvv",
  "cvc",
  "cookie",
  "private_key",
];

// Query/hash parameters that carry credentials (verify-email and impersonate
// links use token_hash; SSO hand-off puts access/refresh tokens in the hash).
const SENSITIVE_PARAM =
  /^(token|token_hash|access_token|refresh_token|id_token|provider_token|code|otp|password|secret|apikey|api_key|key|signature|sig)$/i;

export function isSensitiveKey(key) {
  const normalized = String(key)
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[-\s]/g, "_")
    .toLowerCase();
  return (
    SENSITIVE_KEY_PARTS.some((part) =>
      normalized.replace(/_/g, "").includes(part.replace(/_/g, "")),
    ) || /(^|_)otp(_|$)/.test(normalized)
  );
}

function passesLuhn(digits) {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = Number(digits[i]);
    if (double) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    double = !double;
  }
  return sum % 10 === 0;
}

/** Removes the hash and redacts credential-bearing query parameters. */
export function sanitizeUrl(value) {
  if (typeof value !== "string" || !value) return value;
  try {
    const url = new URL(value);
    url.hash = "";
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_PARAM.test(key) || isSensitiveKey(key)) {
        url.searchParams.set(key, REDACTED);
      }
    }
    return url.toString();
  } catch {
    return sanitizeString(value.split("#")[0]);
  }
}

const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/** Redacts tokens, credential parameters and card numbers inside text. */
export function sanitizeString(value) {
  if (typeof value !== "string" || !value) return value;
  // Record ids are not card numbers: an all-digit UUID prefix can pass Luhn.
  const uuids = [];
  const masked = value.replace(UUID_PATTERN, (uuid) => {
    uuids.push(uuid);
    return `\u0000${uuids.length - 1}\u0000`;
  });
  return masked
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, "[REDACTED_JWT]")
    .replace(/\b(Bearer)\s+[\w.~+/=-]+/gi, `$1 ${REDACTED}`)
    .replace(
      /((?:^|[?&#\s;,])(?:token|token_hash|access_token|refresh_token|id_token|provider_token|code|otp|password|secret|apikey|api_key|key|signature|sig)=)[^&#\s"']*/gi,
      `$1${REDACTED}`,
    )
    .replace(/\b\d(?:[ -]?\d){12,18}\b/g, (match) => {
      const digits = match.replace(/\D/g, "");
      return digits.length >= 13 && digits.length <= 19 && passesLuhn(digits)
        ? "[REDACTED_CARD]"
        : match;
    })
    .replace(/\u0000(\d+)\u0000/g, (_, index) => uuids[Number(index)] ?? "");
}

/**
 * Deep copy with sensitive keys redacted and every string scrubbed. Handles
 * arrays, cycles and Error objects; stops at MAX_DEPTH.
 */
export function sanitizeForLog(value, depth = 0, seen = new WeakSet()) {
  if (typeof value === "string") return sanitizeString(value);
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  if (depth >= MAX_DEPTH) return "[Truncated]";
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForLog(item, depth + 1, seen));
  }

  const source =
    value instanceof Error
      ? { name: value.name, message: value.message, ...value }
      : value;
  const clean = {};
  for (const key of Object.keys(source)) {
    const entry = isSensitiveKey(key)
      ? REDACTED
      : sanitizeForLog(source[key], depth + 1, seen);
    // defineProperty so a "__proto__" key stays an own property.
    Object.defineProperty(clean, key, {
      value: entry,
      enumerable: true,
      configurable: true,
      writable: true,
    });
  }
  return clean;
}
